import { OfferMessage } from '@atomicfinance/bitcoin-dlc-provider';
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/core';
import { generateMnemonic } from 'bip39';
import React, { useRef, useState } from 'react';
import { BetsTableProvider } from '../components/BetsTable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/Table/Table';
import { Bitcoin, createClient } from '../services/chainClient';
import DlcDetectionService from '../services/dlcDetectionService';
import MailchimpService from '../services/mailchimpService';
import TowerService, { getTowerService } from '../services/towerService';
import { getTwitterInfoById } from '../services/twitter';

interface contractData {
  email: string;
  offererEmail: string;
  offererPubkey: string;
  acceptancePubkey: string;
  twitterId: number;
  twitterImage: string;
  twitterName: string;
  twitterUsername: string;
  txid: string;
  transferTxid: string;
  uuid: string;
  stage: string;
  rValue: string;
  localCollateral: number;
  remoteCollateral: number;
  republicanOutcome: number;
  democraticOutcome: number;
  republican: boolean;
}

const NotificationDashboard = () => {
  const bitcoinRef = useRef<Bitcoin | null>(null);
  const tower = useRef<TowerService | null>(null);
  const mailchimp = useRef<MailchimpService | null>(null);
  const dlcDetection = useRef<DlcDetectionService | null>(null);

  const [password, setPassword] = useState('');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(true);
  const [tally, setTally] = useState({});

  const [contractDatum, setContractDatum] = useState([] as contractData[]);

  const loadContractDatum = async () => {
    setIsAuthModalOpen(false);
    const bitcoin = createClient(generateMnemonic(128));
    bitcoinRef.current = bitcoin;
    mailchimp.current = new MailchimpService(password);
    await initializeTower();
    if (tower.current) {
      dlcDetection.current = new DlcDetectionService(tower.current);
    }

    const stageContracts = await dlcDetection.current?.getDlcFundingStages();

    const offers = {};
    const offerList: OfferMessage[] = [];
    const twitterIds = {};
    const offerers = {};
    const counterparties = {};

    const contracts = await tower.current?.getContracts();
    const members = await mailchimp.current?.getMembers();

    await Promise.all(
      contracts.map(async (contract, index) => {
        const offer = await tower.current?.getOffer(contract.UUID);
        const twitterId = await tower.current?.findTwitterId(contract.UUID);
        offers[contract.UUID] = offer;
        offerList.push(offer);

        if (twitterId && twitterId > 0) {
          let _twitterInfo;
          try {
            _twitterInfo = await getTwitterInfoById(twitterId);
          } catch (e) {
            return;
          }

          const { profile_image_url, name, screen_name } = _twitterInfo;

          twitterIds[contract.UUID] = {
            twitterId,
            twitterImage: profile_image_url,
            twitterName: name,
            twitterUsername: screen_name,
          };
        } else {
          twitterIds[contract.UUID] = { twitterId };
        }
      })
    );

    for (let i = 0; i < members.length; i++) {
      const member = members[i];

      const remoteMessages = await tower.current?.getRemoteMessagesWithoutParse(
        member.merge_fields.PUBKEY
      );
      if (remoteMessages && remoteMessages.length > 0) {
        remoteMessages.forEach((acceptance) => {
          const { contractUUID } = acceptance;
          counterparties[contractUUID] = {
            email: member.email_address,
            pubkey: member.merge_fields.PUBKEY,
          };
        });
      }
      const offersResponse = await tower.current?.getOffers(
        member.merge_fields.PUBKEY
      );
      if (offersResponse) {
        const { data: _offers } = offersResponse;

        for (let i = 0; i < _offers.length; i++) {
          const offer = OfferMessage.fromJSON(JSON.parse(_offers[i].offerData));

          offerers[offer.contractId] = {
            email: member.email_address,
            pubkey: member.merge_fields.PUBKEY,
          };
        }
      }
    }

    const tempContractDatum: contractData[] = [];
    contracts.forEach((contract) => {
      const {
        twitterId,
        twitterName,
        twitterImage,
        twitterUsername,
      } = twitterIds[contract.UUID] || {
        twitterId: 0,
        twitterName: '',
        twitterImage: '',
        twitterUsername: '',
      };
      const { email, pubkey } = counterparties[contract.UUID] || {
        email: '',
        pubkey: '',
      };

      const { email: offererEmail } = offerers[contract.UUID] || {
        offererEmail: '',
      };

      const republicanOutcome = offers[contract.UUID]?.outcomes.find(
        (outcome) => outcome.message === 'republican'
      );
      const democraticOutcome = offers[contract.UUID]?.outcomes.find(
        (outcome) => outcome.message === 'democratic'
      );

      tempContractDatum.push({
        email: email || '',
        offererEmail,
        offererPubkey: contract.offererPubkey,
        acceptancePubkey: pubkey || '',
        twitterId: twitterId || 0,
        twitterName: twitterName || '',
        twitterImage: twitterImage || '',
        twitterUsername: twitterUsername || '',
        txid: offers[contract.UUID]?.localPartyInputs.utxos[0].txid || '',
        transferTxid: stageContracts[contract.UUID].txid || '',
        uuid: contract.UUID,
        stage: stageContracts[contract.UUID].stage || '',
        localCollateral:
          offers[contract.UUID]?.localCollateral.GetSatoshiAmount() / 1e8,
        remoteCollateral:
          offers[contract.UUID]?.remoteCollateral.GetSatoshiAmount() / 1e8,
        rValue: offers[contract.UUID]?.oracleInfo.rValue,
        republicanOutcome:
          republicanOutcome.local._satoshis +
          republicanOutcome.remote._satoshis,
        democraticOutcome:
          democraticOutcome.local._satoshis +
          democraticOutcome.remote._satoshis,
        republican: republicanOutcome.local._satoshis > 0,
      });
    });

    const tempTally = { REPUBLICAN: 0, DEMOCRATIC: 0 };

    tempContractDatum.forEach((contract) => {
      if (contract.stage === 'ACTIVE') {
        tempTally['REPUBLICAN'] += contract.republican
          ? contract.localCollateral
          : contract.remoteCollateral;
        tempTally['DEMOCRATIC'] += contract.republican
          ? contract.remoteCollateral
          : contract.localCollateral;
      }
    });

    const stages = ['ACTIVE', 'CANCELLED', 'OFFER', 'ACCEPT', 'FINALIZE'];
    stages.forEach((stage) => {
      tempTally[stage] = tempContractDatum.filter(
        (contract) => contract.stage === stage
      ).length;
    });
    setTally(tempTally);

    setContractDatum(tempContractDatum);
  };

  const initializeTower = async () => {
    if (tower.current) return;
    if (!bitcoinRef.current) throw new Error('Bitcoin client not initialized');

    const bitcoin = bitcoinRef.current;
    const towerInstance = getTowerService(bitcoin);
    await towerInstance.initialize();

    tower.current = towerInstance;
  };

  return (
    <BetsTableProvider>
      <Box>
        <p>
          Active: {tally['ACTIVE']} | Cancelled: {tally['CANCELLED']} | Offers:{' '}
          {tally['OFFER']} | Accepts: {tally['ACCEPT']} | Finalize :{' '}
          {tally['FINALIZE']}
        </p>
        <p>
          Republican: {tally['REPUBLICAN']?.toFixed(8)} | Democratic{' '}
          {tally['DEMOCRATIC']?.toFixed(8)}
        </p>
        <ActiveBets bets={contractDatum} />
        <AuthModal
          isAuthModalOpen={isAuthModalOpen}
          password={password}
          setPassword={setPassword}
          loadContractDatum={loadContractDatum}
        />
      </Box>
    </BetsTableProvider>
  );
};

const AuthModal = ({
  isAuthModalOpen,
  password,
  setPassword,
  loadContractDatum,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      loadContractDatum();
    }
  };

  return (
    <Modal isOpen={isAuthModalOpen} size="xl" isCentered onClose={() => null}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>Authenticate</ModalHeader>
          <ModalBody pb="1rem">
            <VStack spacing={2} alignItems="flex-start">
              <Box>
                <FormLabel fontSize="lg">Unlock Admin Dashboard</FormLabel>
                <Input
                  placeholder="Enter Password Here"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </Box>
            </VStack>

            <Flex mt="1.25em">
              <Button
                ml="auto"
                colorScheme="blue"
                color="white"
                onClick={() => loadContractDatum()}
              >
                Unlock Admin Dashboard
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
};

const OfferedBetRow = ({ bet }) => {
  const electionRValue =
    '0391bd60ce4625bffb466576f290ce7039832212be0c050da5a288868c43e7c824';

  return (
    <TableRow bg={bet.rValue === electionRValue ? 'white' : 'lightgrey'}>
      <TableCell>
        <Text fontSize="sm">{bet.email}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.offererEmail}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.offererPubkey}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.acceptancePubkey}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.twitterId}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.twitterUsername}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.txid}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.transferTxid}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.uuid}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.stage}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.localCollateral}</Text>
      </TableCell>
      <TableCell>
        <Text fontSize="sm">{bet.remoteCollateral}</Text>
      </TableCell>
    </TableRow>
  );
};

const ActiveBets = ({ bets }) =>
  bets.length === 0 ? (
    <TableEmptyState label="No Active Bets" />
  ) : (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Email</TableHeader>
          <TableHeader>Offerer Email</TableHeader>
          <TableHeader>Offer Pubkey</TableHeader>
          <TableHeader>Counterparty Pubkey</TableHeader>
          <TableHeader>Twitter Id</TableHeader>
          <TableHeader>Twitter Username</TableHeader>
          <TableHeader>Txid</TableHeader>
          <TableHeader>Fund/Cancel Txid</TableHeader>
          <TableHeader>UUID</TableHeader>
          <TableHeader>Stage</TableHeader>
          <TableHeader>Local Collateral</TableHeader>
          <TableHeader>Remote Collateral</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {bets.map((bet) => (
          <OfferedBetRow bet={bet} />
        ))}
      </TableBody>
    </Table>
  );

const TableEmptyState = ({ label }) => (
  <Flex alignItems="center" justifyContent="center" bg="#F6F6F6" h="200px">
    <Heading fontWeight={500} size="md" color="#7c7c7c">
      {label}
    </Heading>
  </Flex>
);

export default NotificationDashboard;
